import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Auth, updateProfile } from '@angular/fire/auth';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from 'src/app/auth.service';
import { MySpaceService } from 'src/app/views/my-space/my-space.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-profile-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule
  ],
  templateUrl: './profile-dialog.component.html',
  styleUrls: ['./profile-dialog.component.scss']
})
export class ProfileDialogComponent implements OnInit {
  public itemForm!: UntypedFormGroup;
  public user: any;
  public loading = false;
  public loadingPhoto = false;
  public uploadingPhoto = false;

  private authService = inject(AuthService);
  private mySpaceService = inject(MySpaceService);
  private fb = inject(UntypedFormBuilder);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private angularAuth = inject(Auth);
  public dialogRef = inject(MatDialogRef<ProfileDialogComponent>);

  ngOnInit() {
    // 1. Initialize user with basic info available synchronously
    const basicUser = this.authService.userInfo();
    this.user = {
      ...basicUser,
      firstName: basicUser.fullName?.split(' ')[0] || '',
      lastName: basicUser.fullName?.split(' ').slice(1).join(' ') || ''
    };
    
    // 2. Build form immediately so fields render
    this.buildForm();
    
    // 3. Fetch up-to-date data in the background
    this.getUser();
  }

  getUser() {
    this.loading = true;
    this.loadingPhoto = true;
    
    this.mySpaceService.getUserProfile(this.user._id).subscribe(
      async (user) => {
        this.user = user;
        
        // Update the already rendered form with real data
        this.itemForm.patchValue({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || ''
        });
        
        this.loading = false; // Form data fetched

        // Load photo directly since bucket is public
        if (this.user.photo && this.user.photo !== 'null') {
          this.user.photo = user.photo;
        } else {
          this.user.photo = 'assets/images/no_user.png';
        }
        
        this.loadingPhoto = false;
      },
      () => {
        this.loading = false;
        this.loadingPhoto = false;
      }
    );
  }

  buildForm() {
    this.itemForm = this.fb.group({
      firstName: new UntypedFormControl(this.user.firstName, Validators.required),
      lastName: new UntypedFormControl(this.user.lastName, Validators.required),
      email: new UntypedFormControl({ value: this.user.email, disabled: true }, Validators.required)
    });
  }

  updateProfile(value: any) {
    if (this.itemForm.invalid) return;

    this.loading = true;
    this.mySpaceService
      .updateUserProfile({
        user: this.user,
        formData: value,
      })
      .subscribe(
        () => {
          if (this.angularAuth.currentUser) {
            updateProfile(this.angularAuth.currentUser, {
              displayName: `${value.firstName} ${value.lastName}`.trim()
            }).catch(e => console.error('Failed to update native Firebase Auth profile', e));
          }

          this.toast.snackbar.open(this.translate.instant('Snackbar.ProfileUpdateSuccess'), 'Close', { duration: 2000 });
          this.authService.getUserData(null, true);
          this.dialogRef.close(true);
        },
        () => {
          this.loading = false;
          this.toast.snackbar.open(this.translate.instant('Snackbar.ProfileUpdateFail'), 'Close', { duration: 2000 });
        }
      );
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    this.uploadingPhoto = true;
    
    // Optimistically update the UI with local preview
    const objectUrl = URL.createObjectURL(file);
    const previousPhoto = this.user.photo;
    this.user.photo = objectUrl;

    const extension = file.name.substring(file.name.lastIndexOf('.') + 1);
    const filenamePrefix = this.user._id;
    const filename = filenamePrefix + '.' + extension; // Raw upload name
    const finalFilename = filenamePrefix + '_200x200.webp'; // Force webp for the final link

    this.authService.uploadFile(file, filename, environment.profilePictureBucketName, false).then(() => {
      const fileLink = `https://storage.googleapis.com/${environment.profilePictureBucketName}/${finalFilename}`;
      
      const formData = {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        photo: fileLink
      };

      this.mySpaceService.updateUserProfile({
        user: this.user,
        formData: formData
      }).subscribe(() => {
        if (this.angularAuth.currentUser) {
          // Native user profile linking through Firebase SDK
          updateProfile(this.angularAuth.currentUser, { photoURL: fileLink })
            .catch(e => console.error('Failed to sync photo to Firebase Auth profile', e));
        }

        this.uploadingPhoto = false;
        // Keep the local objectUrl as the display photo until reload or next login
        // to avoid broken image links before the cloud function finishes resizing.
        this.user.photo = objectUrl; 
        this.authService.getUserData(null, true);
      }, () => {
        this.user.photo = previousPhoto;
        this.uploadingPhoto = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
      });
    }).catch(() => {
      this.user.photo = previousPhoto;
      this.uploadingPhoto = false;
      this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Close', { duration: 2000 });
    });
  }
}
